/**
 * NOTE: This code belongs to Kangaroo Rewards. Unauthorized use is prohibited
 */

@file:Suppress("unused", "MemberVisibilityCanBePrivate", "EXPERIMENTAL_API_USAGE")
@file:OptIn(ExperimentalJsExport::class)
package features.getAmazonProductVariants

import kangaroorewards.appsdk.core.api.Api
import kangaroorewards.appsdk.core.domain.Result
import kangaroorewards.appsdk.core.domain.Result.Idle
import kangaroorewards.appsdk.core.domain.Result.Loading
import kangaroorewards.appsdk.core.domain.SerializedResult
import kangaroorewards.appsdk.core.domain.toJsonResult
import kangaroorewards.appsdk.core.utils.CFlow
import kangaroorewards.appsdk.core.utils.asCFlow
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.mapLatest
import kotlinx.serialization.InternalSerializationApi
import kotlin.js.ExperimentalJsExport
import features.getAmazonProductVariants.internal.GetAmazonProductVariantsUseCase
import features.getAmazonProductVariants.internal.GetAmazonProductVariantsUseCase.Params
import features.getAmazonProductVariants.models.RewardResponseModel


typealias GetAmazonProductVariantsState = CFlow<GetAmazonProductVariantsUseCaseResult?>

typealias GetAmazonProductVariantsUseCaseResult = Result<RewardResponseModel>

fun GetAmazonProductVariantsUseCaseResult.serializeGetAmazonProductVariantsApiResult(): SerializedResult<String> {
    return this.toJsonResult<RewardResponseModel>()
}

/**
 * Serializes [GetAmazonProductVariantsState]'s data and returns a new
 * [CFlow] containing data serialized as a JSON [String].
 */
@OptIn(ExperimentalCoroutinesApi::class)
fun GetAmazonProductVariantsState.serializeGetAmazonProductVariantsState(): CFlow<SerializedResult<String>?> {
    return this.mapLatest {
        it?.toJsonResult<RewardResponseModel>()
    }.asCFlow()
}

/**
 * Get a reward with external product variants by external product id [Api] class
 */
class GetAmazonProductVariantsApi : Api() {

    val getAmazonProductVariantsState: GetAmazonProductVariantsState
        get() = _getAmazonProductVariantsState.asCFlow()

    companion object {
        private val _getAmazonProductVariantsState: MutableStateFlow<GetAmazonProductVariantsUseCaseResult> = MutableStateFlow(Idle())
    }

    private val getAmazonProductVariantsUseCase = GetAmazonProductVariantsUseCase()

    /**
     * Get a reward with external product variants by external product id
     */
    suspend fun getAmazonProductVariants(
        overrideHeaders: Map<String, String>? = null,
        rewardId: Int,
        externalProductId: String
    ): Result<RewardResponseModel> {
        /* set state to Loading as soon as function is called */
        _getAmazonProductVariantsState.value = Loading()
        val result = this.getAmazonProductVariantsUseCase(
            Params(
                overrideHeaders = overrideHeaders,
                rewardId = rewardId,
                externalProductId = externalProductId
            )
        ) { result ->
            _getAmazonProductVariantsState.value = result
            return@getAmazonProductVariantsUseCase result
        }
        return result
    }

}
